import { DatePipe, DecimalPipe, DOCUMENT } from '@angular/common';
import { Component, computed, effect, ElementRef, inject, input, linkedSignal, output, signal, viewChild } from '@angular/core';
import type { PersistedCaseHeader, WellsExceptionPayload } from '../../../../../models/persisted-case.model';
import {
  WAT_DATE_FORMAT, toMetricRows, toPersistedCaseTitle,
  toQualityNotice, toSignalLabel, toSignalTelemetry, toSignalTimeline, toTelemetryOverlays,
} from '../../mappers/persisted-case.mapper';
import { ReusableChartComponent } from '../../../../../shared-components/charts/reusable-chart/reusable-chart.component';
import { getChartSeriesColors } from '../../../../../shared-components/charts/chart-series-colors';
import { Uc2OnsetTimelineComponent } from '../uc2-onset-timeline/uc2-onset-timeline.component';
import { Uc2NetworkBladeComponent } from '../uc2-network-blade/uc2-network-blade.component';
import { isComparisonHistoryChart } from '../../mappers/graph-block.mapper';

export type WellExceptionTab = 'workflow' | 'evidence' | 'trends' | 'quality' | 'comments';

@Component({
  selector: 'app-persisted-case-detail',
  imports: [DatePipe, DecimalPipe, Uc2OnsetTimelineComponent, ReusableChartComponent, Uc2NetworkBladeComponent],
  templateUrl: './persisted-case-detail.component.html',
  styleUrl: './persisted-case-detail.component.scss',
})
export class PersistedCaseDetailComponent {
  private _document = inject(DOCUMENT);
  private _workflowHadFocus = false;

  header = input.required<PersistedCaseHeader>();
  payload = input<WellsExceptionPayload>();
  error = input<string>();
  hasWorkflow = input(false);
  workflowTabBadge = input<string | null>(null);
  requestedTab = input<string | null>(null);
  tabSelected = output<string>();
  readonly tabLabels: Record<WellExceptionTab, string> = {
    workflow: 'Workflow', evidence: 'Signal timeline',
    trends: 'Pressure trends', quality: 'Data quality and limitations', comments: 'Comments',
  };
  private _caseId = computed(() => this.header().agentCaseId);
  tabIds = computed<WellExceptionTab[]>(() => [
    'evidence', 'trends', 'quality', ...(this.hasWorkflow() ? ['workflow' as const] : []), 'comments',
  ]);
  activeTab = linkedSignal({
    source: () => ({ caseId: this._caseId(), hasWorkflow: this.hasWorkflow(), requestedTab: this.requestedTab() }),
    computation: (source, previous): WellExceptionTab => {
      if (!previous || previous.source.requestedTab !== source.requestedTab
        || previous.source.caseId !== source.caseId || previous.source.hasWorkflow !== source.hasWorkflow) {
        const requested = this.tabIds().find(tab => tab === source.requestedTab);
        if (requested) return requested;
        if (source.requestedTab !== previous?.source.requestedTab) return 'evidence';
      }
      if (previous?.source.caseId !== source.caseId) return 'evidence';
      return previous.value === 'workflow' && !source.hasWorkflow ? 'evidence' : previous.value;
    },
  });
  selectedSignalIndex = linkedSignal({
    source: () => ({ caseId: this._caseId(), signals: this.payload()?.dossier.signals ?? [] }),
    computation: ({ caseId, signals }, previous): number => {
      if (previous?.source.caseId === caseId) {
        const selectedId = previous.source.signals[previous.value]?.signalId;
        const index = signals.findIndex(signal => signal.signalId === selectedId);
        if (index !== -1) return index;
      }
      const earliest = toSignalTimeline(signals)[0];
      return earliest ? Number(earliest.exceptionId) : 0;
    },
  });
  private _networkBladeOpen = signal(false);
  private _networkContextTrigger = viewChild<ElementRef<HTMLButtonElement>>('networkContextTrigger');
  networkBladeOpen = this._networkBladeOpen.asReadonly();
  readonly dateFormat = WAT_DATE_FORMAT;
  readonly signalLabel = toSignalLabel;
  readonly qualityNotice = toQualityNotice;
  title = computed(() => toPersistedCaseTitle(this.header().title));
  selectedSignal = computed(() => this.payload()?.dossier.signals[this.selectedSignalIndex()] ?? null);
  networkAssetCodes = computed(() => {
    const wells = this.payload()?.dossier.signals.map(signal => signal.context.well_code) ?? [];
    const selected = this.selectedSignal()?.context.well_code;
    return Array.from(new Set(selected ? [selected, ...wells] : wells));
  });
  timelineRows = computed(() => toSignalTimeline(this.payload()?.dossier.signals ?? []));
  metricRows = computed(() => toMetricRows(this.selectedSignal()?.detectorMetrics ?? []));
  parameterRows = computed(() => toMetricRows(this.selectedSignal()?.detectorParameters ?? []));
  wellCount = computed(() => new Set(this.payload()?.dossier.signals.map(signal => signal.context.well_code) ?? []).size);
  facility = computed(() => this.header().facilityName ?? 'Facility not specified');
  telemetry = computed(() => {
    const dossier = this.payload()?.dossier;
    const seriesColor = getComputedStyle(this._document.documentElement).getPropertyValue('--chv-blue-light').trim();
    return dossier?.signals.map(signal => toSignalTelemetry(signal, dossier.citations, seriesColor))
      .sort((a, b) => Date.parse(a.signal.episodeOnsetUtc) - Date.parse(b.signal.episodeOnsetUtc)) ?? [];
  });
  visibleTelemetry = computed(() => this.telemetry().map(entry => ({
    ...entry,
    charts: entry.charts.filter(chart => !isComparisonHistoryChart(chart.graph.title)),
  })));
  telemetryOverlays = computed(() => {
    const theme = getComputedStyle(this._document.documentElement);
    const colors = getChartSeriesColors(theme);
    return toTelemetryOverlays(this.telemetry(), colors);
  });

  constructor() {
    effect(() => {
      if (!this.hasWorkflow() && this._workflowHadFocus) {
        this._workflowHadFocus = false;
        const caseId = this._caseId();
        queueMicrotask(() => {
          const focused = this._document.activeElement;
          if (!this.hasWorkflow() && this._caseId() === caseId
            && (focused === this._document.body
              || focused?.closest('#uc2-tab-workflow, #uc2-panel-workflow'))) {
            this._document.getElementById('uc2-tab-evidence')?.focus();
          }
        });
      }
    });
    effect(() => {
      this._caseId();
      this._networkBladeOpen.set(false);
    });
  }

  selectTimelineSignal(index: string): void {
    this.selectedSignalIndex.set(Number(index));
  }

  selectTab(tab: WellExceptionTab): void {
    if (this.tabIds().includes(tab)) {
      this.activeTab.set(tab);
      this.tabSelected.emit(tab);
    }
  }

  onDetailFocus(event: FocusEvent): void {
    const target = event.target;
    this._workflowHadFocus = target instanceof Element
      && !!target.closest('#uc2-tab-workflow, #uc2-panel-workflow');
  }

  onTabKeydown(event: KeyboardEvent, tab: WellExceptionTab): void {
    const tabs = this.tabIds();
    const index = tabs.indexOf(tab);
    let next: WellExceptionTab;
    switch (event.key) {
      case 'ArrowRight': next = tabs[(index + 1) % tabs.length]; break;
      case 'ArrowLeft': next = tabs[(index + tabs.length - 1) % tabs.length]; break;
      case 'Home': next = tabs[0]; break;
      case 'End': next = tabs[tabs.length - 1]; break;
      default: return;
    }
    event.preventDefault();
    this.selectTab(next);
    this._document.getElementById(`uc2-tab-${next}`)?.focus();
  }

  onNetworkContextOpened(): void {
    this._networkBladeOpen.set(true);
  }

  onNetworkContextClosed(): void {
    this._networkBladeOpen.set(false);
    queueMicrotask(() => this._networkContextTrigger()?.nativeElement.focus());
  }
}
